import React from 'react';
import HBMediaElement from '../../components/HBMediaElement/HBMediaElement';
import {
    LARGE_AVATAR,
    SMALL_AVATAR,
    STATUS_INDICATOR_SIZE,
    STATUS_INDICATOR_BORDER,
    BrandAvatarWrapper,
    InnerBrandAvatarWrapper,
    InitialsLogo,
    StyledStatusIndicator
} from './HBBrandAvatar.styles';
import type { HBAvatarSize, HBBrandAvatarProps } from './HBBrandAvatar.types';

const HBBrandAvatar = ({
    company: {
        company_name: companyName,
        icon_image: iconImage,
        brand_color: brandColor
    },
    size = 'small',
    status
}: HBBrandAvatarProps) => {
    const companyInitial = companyName?.charAt(0).toUpperCase() || '';
    const clipPath = status ? getIndicatorClipPath(size) : '';

    const isDefaultIcon = iconImage?.is_default_company_icon;
    const iconPath =
        !isDefaultIcon && (iconImage?.cloudinary_public_id || iconImage?.url);

    return (
        <BrandAvatarWrapper $size={size} $clipPath={clipPath}>
            <InnerBrandAvatarWrapper $clipPath={clipPath}>
                {iconPath ? (
                    <HBMediaElement
                        path={iconPath}
                        width={iconImage.position_w}
                        height={iconImage.position_h}
                        x={iconImage.position_x}
                        y={iconImage.position_y}
                        crop="crop"
                    />
                ) : (
                    <InitialsLogo
                        $brandColor={brandColor}
                        $size={size}
                        data-avatar-company-name={companyName}
                    >
                        <span>{companyInitial}</span>
                    </InitialsLogo>
                )}
            </InnerBrandAvatarWrapper>
            {status && (
                <StyledStatusIndicator
                    status={status}
                    data-avatar-status={status}
                />
            )}
        </BrandAvatarWrapper>
    );
};

export default HBBrandAvatar;

/**
 * Generates a clip-path for the avatar to create a corner cutout for the status indicator.
 * The path creates a shape that:
 * 1. Starts at top-left (0,0)
 * 2. Goes straight to the right until the cutout
 * 3. Creates a rounded corner cutout using an arc
 * 4. Continues to bottom-right
 * 5. Goes straight down to bottom-left
 * 6. Closes the path back to start
 */
const getIndicatorClipPath = (size: HBAvatarSize): string => {
    const avatarSize = size === 'large' ? LARGE_AVATAR : SMALL_AVATAR;
    const indicatorSize = STATUS_INDICATOR_SIZE / 2 + STATUS_INDICATOR_BORDER;
    const cutoutStart = avatarSize - indicatorSize;

    // SVG path commands:
    // M = Move to
    // L = Line to
    // A = Arc (rx ry x-axis-rotation large-arc-flag sweep-flag x y)
    // Z = Close path
    return `
        clip-path: path(
            'M 0 0 
             L ${cutoutStart} 0 
             A ${indicatorSize} ${indicatorSize} 0 0 0 ${avatarSize} ${indicatorSize} 
             L ${avatarSize} ${avatarSize} 
             L 0 ${avatarSize} 
             Z'
        );
    `;
};
